import {useCallback, useEffect, useRef, useState} from 'react';
import Honeybadger from '@honeybadger-io/js';
import useFinancialAccounts from '../useFinancialAccounts';
import CashApi from '../../services/CashApi';

/**
 * Encapsulates all state, API calls, and side effects for GET /v2/account-balances.
 *
 * Consumed by AccountBalancesContext so the Provider component stays a thin wrapper.
 * On any failure (network error, non-2xx, or upstream 500/502 from api-cash) this
 * intentionally does NOT throw and does NOT surface a user-facing message - callers
 * are expected to hide the balance-driven UI silently and rely on Honeybadger for
 * observability
 */
const useAccountBalances = () => {
  const {
    isEnabled: isFinancialAccountsEnabled,
    isResolved: isFinancialAccountsResolved,
  } = useFinancialAccounts();
  const [accountBalances, setAccountBalances] = useState(undefined);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState(null);
  const inFlightRequest = useRef(null);

  const fetchAccountBalances = useCallback(async () => {
    if (!isFinancialAccountsResolved) return null;

    if (!isFinancialAccountsEnabled) {
      setAccountBalances(null);
      setError(null);
      setIsFetching(false);

      return null;
    }

    if (inFlightRequest.current) {
      return inFlightRequest.current;
    }

    setIsFetching(true);

    const request = (async () => {
      try {
        const response = await CashApi.getAccountBalances();

        if (!response.ok) {
          throw new Error(response.statusText);
        }

        const payload = await response.json();

        setAccountBalances(payload);
        setError(null);

        return payload;
      } catch (caughtError) {
        Honeybadger.notify(caughtError, 'FetchAccountBalancesError');

        setAccountBalances(null);
        setError(caughtError);

        return null;
      } finally {
        inFlightRequest.current = null;
        setIsFetching(false);
      }
    })();

    inFlightRequest.current = request;

    return request;
  }, [isFinancialAccountsEnabled, isFinancialAccountsResolved]);

  useEffect(() => {
    if (accountBalances === undefined && isFinancialAccountsResolved) {
      fetchAccountBalances();
    }
  }, [accountBalances, fetchAccountBalances, isFinancialAccountsResolved]);

  return {
    accountBalances: accountBalances ?? null,
    isFetching,
    error,
    fetchAccountBalances,
  };
};

export default useAccountBalances;
